import type { ContentDeviceEvent, DeviceSession } from "@teamtable/api";

const MAX_QUEUE_SIZE = 500;
const encoder = new TextEncoder();

export function serializedBytes(value: unknown): number {
  return encoder.encode(JSON.stringify(value)).byteLength;
}

function generateUUID(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const queue: ContentDeviceEvent[] = [];
let contextProperties: Record<string, unknown> = {};

export function setEventContext(session: DeviceSession | null) {
  const context = session?.game_context;
  contextProperties = context
    ? {
        fleet_id: context.fleet.id,
        fleet_slug: context.fleet.slug,
        venue_id: context.venue.id,
        venue_slug: context.venue.slug,
        sound_override: context.settings.sound_override,
        hardware_version: context.hardware_version,
        player_card_layout_id: context.settings.player_card_layout_id,
        context_revision: session?.context_revision,
      }
    : {};
}

export function trackEvent(name: string, properties: Record<string, unknown> = {}): void {
  const event: ContentDeviceEvent = {
    client_id: generateUUID(),
    event_name: name,
    properties: structuredClone({ ...properties, ...contextProperties }),
    client_timestamp: new Date().toISOString(),
  };

  queue.push(event);

  if (queue.length > MAX_QUEUE_SIZE) {
    queue.splice(0, queue.length - MAX_QUEUE_SIZE);
  }
}

/** Take a FIFO batch whose JSON array fits the UTF-8 byte budget. */
export function drainQueue(maxBytes = Infinity): ContentDeviceEvent[] {
  if (maxBytes === Infinity) return queue.splice(0, queue.length);
  const events: ContentDeviceEvent[] = [];
  let bytes = 2; // []
  while (queue.length > 0) {
    const event = queue[0];
    let eventBytes: number;
    try {
      eventBytes = serializedBytes(event);
    } catch {
      queue.shift();
      console.warn("Dropping analytics event that cannot be serialized", event.event_name);
      continue;
    }
    if (eventBytes + 2 > maxBytes) {
      // A single unshippable event must not block later events or state refresh.
      queue.shift();
      console.warn("Dropping analytics event exceeding heartbeat byte budget", event.event_name);
      continue;
    }
    const additionalBytes = eventBytes + (events.length > 0 ? 1 : 0);
    if (bytes + additionalBytes > maxBytes) break;
    events.push(queue.shift()!);
    bytes += additionalBytes;
  }
  return events;
}

export function restoreEvents(events: ContentDeviceEvent[]): void {
  queue.unshift(...events);

  if (queue.length > MAX_QUEUE_SIZE) {
    queue.splice(0, queue.length - MAX_QUEUE_SIZE);
  }
}
